import { useLingui } from '@lingui/react/macro';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { diamondGlows, DiamondGrid, PillLink } from '../primitives';
import { useSolutions, type Solution, type SolutionCard } from './solutions-data';

/** Shared by every card kind so a photo, a step and a screen all occupy the same footprint. */
const CARD = 'overflow-hidden rounded-3xl';

/**
 * One step card. Geometry is the hand-off's, measured not guessed: 24px padding, the illustration
 * slot 168x182 in the top-left corner, the step number right-aligned on the same line, and the title
 * plus copy pinned to the bottom 32px from the edge.
 */
function StepCard(props: { card: Extract<SolutionCard, { kind: 'step' }>; number?: string }) {
	return (
		<article className={`bg-sand relative flex min-h-[391px] flex-col p-6 pb-8 ${CARD}`}>
			{props.number && (
				<span className="text-ink/50 absolute top-[27px] right-6 text-sm leading-[1.3] font-medium">
					{props.number}
				</span>
			)}
			{/* The hand-off's illustration slot, 168x182. `object-contain` because the drawings do NOT share
			    one canvas — Figma sizes each to its own artwork (168x182, 193x193, 212x98, …) — so fitting
			    them to the slot is what keeps every card's drawing at a comparable weight without squashing
			    the wide ones. The drawing is decorative: the title right below carries the meaning, so it is
			    hidden from readers rather than given an alt that would repeat the heading. */}
			<div className="flex h-[182px] w-[168px] shrink-0 items-center justify-center">
				<img src={props.card.art} alt="" aria-hidden className="h-full w-full object-contain" />
			</div>
			{/* Flows straight after the 182px illustration slot rather than being pinned to the bottom with
			    `mt-auto`. Pinned, what lines up across the row is the LAST line of the copy, so a card whose
			    description runs to four lines pushes its own title a line higher than its neighbours'. The
			    slot's height is fixed, so starting from the top puts every title on the same baseline and
			    leaves the slack at the foot of the card instead. */}
			<div className="flex flex-col gap-2 pt-6">
				<h3 className="text-2xl leading-[1.3] font-medium">{props.card.title}</h3>
				<p className="text-base leading-[1.5]">{props.card.description}</p>
			</div>
		</article>
	);
}

/** A photo filling a whole slot, same size as a step card. */
function MediaCard(props: { card: Extract<SolutionCard, { kind: 'media' }> }) {
	return (
		<div className={`bg-sand min-h-[391px] ${CARD}`}>
			<Image
				src={props.card.src}
				alt={props.card.alt}
				height={782}
				aspectRatio={309 / 391}
				objectFit="cover"
				className="h-full w-full object-cover!"
			/>
		</div>
	);
}

/**
 * Product interface on a dark device frame. The asset IS the whole card — ink frame, rounded corners
 * and both panels — so there is no geometry to reproduce here. `object-contain` rather than `cover`
 * because the panels must never be cropped; the letterboxing it leaves is invisible against `bg-ink`,
 * the same colour as the frame drawn inside the image.
 */
function ScreenCard(props: { card: Extract<SolutionCard, { kind: 'screen' }> }) {
	const { language } = useTranslation();
	const src = props.card.src[language] ?? props.card.src.en;
	return (
		<div className={`bg-ink relative min-h-[391px] ${CARD}`}>
			<img src={src} alt={props.card.alt} className="absolute inset-0 size-full object-contain" />
		</div>
	);
}

function Panel(props: { solution: Solution }) {
	const cards = props.solution.cards;
	// The hand-off numbers the steps only on the two channels whose four slots are ALL steps; on the
	// mixed panels the text cards carry no number, and numbering two of four would invent a sequence.
	const numbered = cards.every((c) => c.kind === 'step');

	return (
		<div className="relative overflow-hidden rounded-3xl bg-white p-6 md:p-12">
			{/* The dotted texture over the top-right corner. Figma draws it as the same 777x501 glow every
			    other section uses, centred 78.5px left of the panel's right edge and 113.5px above its top
			    — so only the glow's lower-left quadrant lands inside. `opacity` 1: the glow's own 0.4 alpha
			    is the intensity. */}
			<DiamondGrid opacity={1} masks={[diamondGlows({ x: 'calc(100% - 78.5px)', y: '-113.5px' })]} />

			<div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
				{/* Wide when a CTA shares the line, a little narrower otherwise — those are the two boxes the
				    hand-off draws, rather than an arbitrary width per tab. The narrow one is 800px, NOT the
				    620 it was: at 620 the copy ran to four lines where the design has three. */}
				<p
					className={`text-xl leading-[1.3] font-medium md:text-2xl ${props.solution.cta ? 'max-w-[955px]' : 'max-w-[800px]'}`}
				>
					{props.solution.description}
				</p>
				{props.solution.cta && (
					<PillLink to={props.solution.cta.to} variant="outline" className="shrink-0">
						{props.solution.cta.label}
					</PillLink>
				)}
			</div>

			<div className="relative mt-8 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
				{cards.map((card, i) => {
					// Keyed on the title or the asset: a panel never repeats either, since four identical slots
					// would be a design mistake, not a layout to support.
					if (card.kind === 'step') {
						return (
							<StepCard key={card.title} card={card} number={numbered ? String(i + 1).padStart(2, '0') : undefined} />
						);
					}
					if (card.kind === 'screen') return <ScreenCard key={card.src.en} card={card} />;
					return <MediaCard key={card.src} card={card} />;
				})}
			</div>
		</div>
	);
}

/**
 * "Elige tu forma de sumar" — one tab per channel a donation can travel through, each opening onto
 * the four slots that explain it.
 *
 * Built on the shared (Radix) Tabs so arrow-key navigation and the tab/panel wiring come for free;
 * the classes reset its app-UI chrome down to the hand-off's pills (56px tall, 24px sides, 16px
 * radius, ink when active). The strip scrolls sideways on small screens rather than wrapping into a
 * block of stacked buttons.
 */
export function SolutionTabs() {
	const { t } = useLingui();
	const solutions = useSolutions();

	return (
		<section className="bg-apricot">
			<div className="mx-auto max-w-[1440px] px-6 py-16 md:py-20 xl:px-10">
				<h2 className="text-center text-3xl leading-[1.2] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Choose your way of adding up`}
				</h2>

				<Tabs defaultValue={solutions[0]?.key} className="mt-10 gap-2 md:mt-12">
					<TabsList
						variant="line"
						className="hide-scrollbar mx-auto max-w-full flex-nowrap justify-start gap-2 overflow-x-auto rounded-none border-0 bg-transparent p-0 group-data-horizontal/tabs:h-auto md:justify-center"
					>
						{solutions.map((s) => (
							<TabsTrigger
								key={s.key}
								value={s.key}
								// #FBE8D7 sampled off the mockup, opaque. The hand-off writes this as a 50% white, which
								// happens to land on almost the same colour over `bg-apricot` — but only over
								// `bg-apricot`, so the pills would drift the day this section changes background.
								//
								// EVERY background here has to carry the `group-data-[variant=line]/tabs-list:` prefix.
								// `cn` is tailwind-merge, and it only dedupes classes whose modifiers match: a bare
								// `bg-[#FBE8D7]` does not cancel the trigger's own `…:bg-transparent`, so both survive
								// and the library's wins on specificity (0,3,0 vs 0,1,0) — the pills then render as bare
								// apricot. Prefixed, it replaces that class outright.
								//
								// The four rules are ordered by specificity on purpose: inactive (0,3,0) < hover (0,4,0),
								// and active+hover (0,5,0) is what keeps the selected pill dark under the cursor.
								className="text-ink group-data-[variant=line]/tabs-list:data-active:bg-ink group-data-[variant=line]/tabs-list:data-active:hover:bg-ink h-14 flex-none rounded-2xl px-6 text-base font-medium group-data-[variant=line]/tabs-list:bg-[#FBE8D7] after:hidden group-data-[variant=line]/tabs-list:hover:bg-white/70 data-active:text-white"
							>
								{s.tab}
							</TabsTrigger>
						))}
					</TabsList>

					{solutions.map((s) => (
						<TabsContent key={s.key} value={s.key}>
							<Panel solution={s} />
						</TabsContent>
					))}
				</Tabs>
			</div>
		</section>
	);
}
